import { api } from "../api";
import type { PaginationParams, PaginatedResponse } from "@/types/common";
import type { Company } from "@/types/company";
import type { Region } from "@/types/region";
import type {
  Organization,
  CreateOrganizationInput,
} from "@/types/organization";

type OrganizationsApiResponse = Organization[] | PaginatedResponse<Organization>;

interface AdminScopeOptions {
  adminScope?: boolean;
}

function isPaginated(payload: unknown): payload is PaginatedResponse<Organization> {
  return !!payload && typeof payload === "object" && Array.isArray((payload as PaginatedResponse<Organization>).data);
}

export const organizationApi = {
  getAll: async (options?: AdminScopeOptions) => {
    const response = await api.get<OrganizationsApiResponse>("/organizations", { adminScope: options?.adminScope });
    return isPaginated(response) ? response.data : response;
  },

  paginate: (params?: PaginationParams & { isActive?: boolean }, options?: AdminScopeOptions): Promise<PaginatedResponse<Organization>> => {
    const query = new URLSearchParams();
    if (params) {
      Object.entries(params).forEach(([k, v]) => {
        if (v !== undefined && v !== null) query.set(k, String(v));
      });
    }
    const qs = query.toString();
    return api.get<PaginatedResponse<Organization>>(`/organizations${qs ? `?${qs}` : ""}`, { adminScope: options?.adminScope });
  },

  getById: (id: number) => api.get<Organization>(`/organizations/${id}`),
  getCompanies: (id: number) => api.get<Company[]>(`/organizations/${id}/companies`),
  getRegions: (id: number) => api.get<Region[]>(`/organizations/${id}/regions`),
  create: (data: CreateOrganizationInput) =>
    api.post<Organization>("/organizations", data),
  update: (id: number, data: Partial<Organization>) =>
    api.patch<Organization>(`/organizations/${id}`, data),
  /** Branding only (T-1301): a door an org admin may use on their own org. `null` clears a field. */
  updateBranding: (
    id: number,
    data: {
      logoUrl?: string | null;
      brandPrimaryColor?: string | null;
      brandSecondaryColor?: string | null;
    },
  ) => api.patch<Organization>(`/organizations/${id}/branding`, data),
  delete: (id: number) => api.delete(`/organizations/${id}`),

  getGlobalStats: async (options?: AdminScopeOptions): Promise<{
    total: number;
    active: number;
    inactive: number;
  }> => {
    const stats = await api.get<{
      organizations: { total: number; active: number; inactive: number };
    }>("/organizations/stats", { adminScope: options?.adminScope });
    return stats.organizations;
  },
};
